import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Dashboard customize controller
// Handles: Customize/Lock mode toggle, SortableJS drag-and-drop, position persistence
export default class extends Controller {
  static targets = ["grid", "customizeBtn", "lockBtn", "widget"]
  static values = { reorderUrl: String, editing: { type: Boolean, default: false } }

  connect() {
    this.sortable = null
    this.handleEscape = (e) => {
      if (e.key === "Escape" && this.editingValue) this.lock()
    }
    document.addEventListener("keydown", this.handleEscape)
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleEscape)
    this._destroySortable()
  }

  // ── Customize mode ───────────────────────────────────────────────
  customize() {
    this.editingValue = true
    this.element.setAttribute("data-editing", "true")

    // Show drag handles + remove buttons on each widget
    this.widgetTargets.forEach(w => {
      w.classList.add("widget-editing")
      if (w.tagName === "DETAILS") w.open = true
    })

    // Show/hide buttons
    if (this.hasCustomizeBtnTarget) this.customizeBtnTarget.classList.add("hidden")
    if (this.hasLockBtnTarget) this.lockBtnTarget.classList.remove("hidden")

    // Init SortableJS on the grid
    if (this.hasGridTarget) {
      this.sortable = Sortable.create(this.gridTarget, {
        animation: 200,
        handle: ".widget-drag-handle",
        ghostClass: "widget-ghost",
        chosenClass: "widget-chosen",
        dragClass: "widget-drag",
        onEnd: (evt) => this._saveOrder()
      })
    }
  }

  // ── Lock / save ──────────────────────────────────────────────────
  lock() {
    this.editingValue = false
    this.element.removeAttribute("data-editing")

    this.widgetTargets.forEach(w => {
      w.classList.remove("widget-editing")
      if (w.tagName === "DETAILS" && window.innerWidth < 768) w.open = false
    })

    if (this.hasCustomizeBtnTarget) this.customizeBtnTarget.classList.remove("hidden")
    if (this.hasLockBtnTarget) this.lockBtnTarget.classList.add("hidden")

    this._destroySortable()
    this._saveOrder()
  }

  // ── Remove widget ────────────────────────────────────────────────
  async removeWidget(event) {
    const btn = event.currentTarget
    const widgetEl = btn.closest("[data-widget-id]")
    if (!widgetEl) return

    const widgetId = widgetEl.dataset.widgetId
    const removeUrl = btn.dataset.removeUrl

    if (!removeUrl) return

    try {
      const resp = await fetch(removeUrl, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": this._csrfToken(),
          "Accept": "application/json"
        }
      })
      if (resp.ok) {
        widgetEl.style.transition = "opacity 0.2s, transform 0.2s"
        widgetEl.style.opacity = "0"
        widgetEl.style.transform = "scale(0.95)"
        setTimeout(() => widgetEl.remove(), 200)
      }
    } catch (e) {
      console.error("Widget remove failed", e)
    }
  }

  // ── Private ──────────────────────────────────────────────────────
  async _saveOrder() {
    if (!this.reorderUrlValue || !this.hasGridTarget) return

    const ids = Array.from(this.gridTarget.querySelectorAll("[data-widget-id]"))
      .map(el => el.dataset.widgetId)
      .filter(Boolean)

    if (!ids.length) return

    try {
      await fetch(this.reorderUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this._csrfToken(),
          "Accept": "application/json"
        },
        body: JSON.stringify({ widget_ids: ids })
      })
    } catch (e) {
      console.error("Widget reorder failed", e)
    }
  }

  _destroySortable() {
    if (this.sortable) {
      this.sortable.destroy()
      this.sortable = null
    }
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
